/**
 * Value helpers for the resolve-with-value dialog: waste-code parsing and
 * serialization, and normalizeForCompare, the function every client-side
 * "is this the same value?" comparison goes through.
 */
import { normalizeStateWasteCodes } from "encamp-shared/src/waste/stateWasteCodeNormalization";

import type { Q1Answer } from "./resolveWithValue.helpers";

// ---------------------------------------------------------------------------
// Q1 answer
// ---------------------------------------------------------------------------

const Q1_ANSWER_VALUES: readonly string[] = [
  "EMANIFEST",
  "VENDOR",
  "DOCUMENT",
  "SOMETHING_ELSE",
  "DISMISS",
  "CANT_DETERMINE",
  "BOX_18A",
];

/** Narrows a RadioGroup's plain string change value to the Q1Answer union. */
export function isQ1Answer(value: string): value is Q1Answer {
  return Q1_ANSWER_VALUES.includes(value);
}

// ---------------------------------------------------------------------------
// Waste codes
// ---------------------------------------------------------------------------

const WASTE_CODE_FIELDS = new Set(["federalWasteCodes", "stateWasteCodes"]);

export function isWasteCodeField(field: string): boolean {
  return WASTE_CODE_FIELDS.has(field);
}

/** Best-effort parse of a stored JSON-array waste code string. Never throws. */
export function parseWasteCodes(value: string | null | undefined): string[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === "string") : [];
  } catch {
    return [];
  }
}

/** Trims, dedupes, sorts, and serializes codes to the pipeline's stored JSON-array-string shape. */
export function serializeWasteCodes(codes: readonly string[]): string {
  const cleaned = [...new Set(codes.map((c) => c.trim()).filter(Boolean))].sort();
  return JSON.stringify(cleaned);
}

// Comparison normalization: per-field rules for deciding two values are the same.

/**
 * Sentinel/informational waste-code markers the server's
 * stripSentinelWasteCodes drops. Duplicated here since oak doesn't depend
 * on the server-only package that defines them.
 */
const WASTE_CODE_COMPARE_SENTINELS = new Set(["EXEMPT", "NONE", "NULL", "N/A", "", "1"]);

function dropSentinelWasteCodesForCompare(codes: readonly string[]): string[] {
  return codes.filter((code) => {
    const upper = code.trim().toUpperCase();
    return upper.length > 0 && !WASTE_CODE_COMPARE_SENTINELS.has(upper);
  });
}

function normalizeQuantityForCompare(value: string): string {
  const trimmed = value.trim();
  // Number("") is 0, not NaN. Comparing raw for empty input keeps an absent
  // value from reading as equal to an explicit "0".
  if (trimmed === "") return trimmed;
  // Number(...) is deliberate: this field never carries thousands separators.
  // ast-grep-ignore: no-unsafe-number-parsing
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? String(parsed) : trimmed;
}

function normalizeWasteCodesForCompare(field: string, value: string): string {
  const codes = parseWasteCodes(value);
  const canonical =
    field === "stateWasteCodes" ? normalizeStateWasteCodes(codes) : [...new Set(codes)].sort();
  return JSON.stringify(dropSentinelWasteCodesForCompare(canonical));
}

/**
 * Per-field comparison form: quantity compares as a number, waste codes
 * compare deduped/sorted/sentinel-free, everything else compares trimmed.
 */
export function normalizeForCompare(field: string, value: string): string {
  if (field === "quantity") return normalizeQuantityForCompare(value);
  if (isWasteCodeField(field)) return normalizeWasteCodesForCompare(field, value);
  return value.trim();
}

/**
 * True when a normalized manual value already matches the row's document
 * value (false with none). The client then sends correctValueSource DOCUMENT
 * with no correctValue, MANUAL otherwise.
 */
export function isSameAsDocument(
  field: string,
  normalizedValue: string,
  documentValue: string | null | undefined
): boolean {
  if (documentValue == null) return false;
  return normalizeForCompare(field, normalizedValue) === normalizeForCompare(field, documentValue);
}

/**
 * Whether the document disagrees with e-Manifest, by the server's rule: the
 * persisted verdict, or raw inequality for legacy rows whose verdict is null.
 */
export function documentDisagrees(row: {
  eManifestValue?: string | null;
  manifestDocumentValue?: string | null;
  documentDiscrepant?: boolean | null;
}): boolean {
  return (
    row.documentDiscrepant ??
    (row.manifestDocumentValue != null && row.manifestDocumentValue !== row.eManifestValue)
  );
}
